/**
 * Prices travel as minor units (150.00 EUR is 15000) in the business currency. These helpers
 * turn them into something a person reads, and turn what a person types back into minor units.
 */

const CURRENCY_CODE = /^[A-Z]{3}$/;

/** Decimal places the currency uses: 2 for EUR, 0 for JPY. Falls back to 2 for a bad code. */
export const currencyDigits = (currency: string, locale = "en") => {
  if (!CURRENCY_CODE.test(currency)) return 2;
  return new Intl.NumberFormat(locale, { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
};

/** 15000 + "EUR" -> "€150.00" in the reader's locale. */
export const formatMoney = ({ amount, currency, locale }: { amount: number; currency: string; locale: string }) => {
  if (!CURRENCY_CODE.test(currency)) {
    return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount / 100);
  }
  const digits = currencyDigits(currency, locale);
  return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount / 10 ** digits);
};

export type PriceLabel =
  | { kind: "none" }
  | { kind: "free" }
  | { kind: "fixed"; price: string }
  | { kind: "from"; price: string };

/**
 * What a service card says about money. No amount shows nothing at all (plenty of businesses
 * talk about price in person), zero is "Free", and a starting price reads "From 150.00".
 */
export const describePrice = ({
  amount,
  from,
  currency,
  locale,
}: {
  amount: number | null | undefined;
  from: boolean | undefined;
  currency: string;
  locale: string;
}): PriceLabel => {
  if (amount === null || amount === undefined) return { kind: "none" };
  if (amount === 0 && !from) return { kind: "free" };
  const price = formatMoney({ amount, currency, locale });
  return from ? { kind: "from", price } : { kind: "fixed", price };
};

/** 15000 + "EUR" -> "150.00", the plain text the price field edits. */
export const moneyInputValue = (amount: number | null | undefined, currency: string) => {
  if (amount === null || amount === undefined) return "";
  const digits = currencyDigits(currency);
  return (amount / 10 ** digits).toFixed(digits);
};

const MAX_AMOUNT = 100_000_000;

export type ParsedMoney = { ok: true; amount: number | null } | { ok: false };

/**
 * Reads what someone typed into the price field. Empty means "no price". Accepts "150",
 * "150.5", "150,50", and "1 200,00": the last dot or comma is the decimal point when it has no
 * more digits after it than the currency allows, otherwise it was a thousands separator.
 */
export const parseMoneyInput = (text: string, currency: string): ParsedMoney => {
  const cleaned = text.replace(/[\s ']/g, "");
  if (!cleaned) return { ok: true, amount: null };

  const digits = currencyDigits(currency);
  const lastSeparator = Math.max(cleaned.lastIndexOf(","), cleaned.lastIndexOf("."));
  let whole = cleaned;
  let fraction = "";
  if (lastSeparator >= 0) {
    const tail = cleaned.slice(lastSeparator + 1);
    const head = cleaned.slice(0, lastSeparator).replace(/[.,]/g, "");
    if (tail.length > digits) {
      whole = head + tail;
    } else {
      whole = head || "0";
      fraction = tail;
    }
  }

  if (!/^\d+$/.test(whole) || (fraction && !/^\d+$/.test(fraction))) return { ok: false };
  const amount = Number(whole) * 10 ** digits + Number(fraction.padEnd(digits, "0") || "0");
  if (!Number.isSafeInteger(amount) || amount > MAX_AMOUNT) return { ok: false };
  return { ok: true, amount };
};
